import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Search, PlusCircle, Truck } from 'lucide-react';
import EquipmentRatePicker from './EquipmentRatePicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/material-sheets`;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Type-ahead on the master consumables list; picks are priced at the catalog sell price.
export default function MaterialLinePicker({ onAdd, disabled }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [openList, setOpenList] = useState(false);
  const [custom, setCustom] = useState(null);
  const [mode, setMode] = useState('consumable');
  const [eqPick, setEqPick] = useState({ rate_basis: 'day', qty: 1 });
  const box = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (!term) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await axios.get(`${API}/catalog/search`, { params: { q: term, limit: 20 } }); setResults(r.data || []); setOpenList(true); }
      catch { setResults([]); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpenList(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const pick = (c) => {
    onAdd({ catalog_id: c.id, name: c.name, part_number: c.part_number, category: c.category, unit: c.unit, unit_price: c.unit_price, qty: 1 });
    setQ(''); setResults([]); setOpenList(false);
  };
  const startCustom = () => { setCustom({ name: q.trim(), unit_price: '', unit: 'each', qty: 1 }); setOpenList(false); };
  const addCustom = () => {
    if (!custom.name.trim()) return;
    onAdd({ name: custom.name.trim(), unit: custom.unit || 'each', unit_price: Number(custom.unit_price) || 0, qty: Number(custom.qty) || 1, price_overridden: true });
    setCustom(null); setQ('');
  };

  if (custom) {
    return (
      <div className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 p-3 grid gap-2 sm:grid-cols-[1fr_90px_110px_110px_auto] items-end" data-testid="ms-custom-line">
        <label className="text-xs font-semibold text-amber-900">Item (not in the catalog)
          <input autoFocus value={custom.name} onChange={e => setCustom({ ...custom, name: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-amber-300 bg-white text-slate-900 text-sm" data-testid="ms-custom-name" /></label>
        <label className="text-xs font-semibold text-amber-900">Qty
          <input type="number" min="0" step="any" value={custom.qty} onChange={e => setCustom({ ...custom, qty: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-amber-300 bg-white text-slate-900 text-sm" data-testid="ms-custom-qty" /></label>
        <label className="text-xs font-semibold text-amber-900">Unit
          <input value={custom.unit} onChange={e => setCustom({ ...custom, unit: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-amber-300 bg-white text-slate-900 text-sm" data-testid="ms-custom-unit" /></label>
        <label className="text-xs font-semibold text-amber-900">Sell price
          <input type="number" min="0" step="0.01" value={custom.unit_price} onChange={e => setCustom({ ...custom, unit_price: e.target.value })} placeholder="0.00" className="mt-1 w-full px-3 py-2 rounded-lg border border-amber-300 bg-white text-slate-900 text-sm" data-testid="ms-custom-price" /></label>
        <div className="flex gap-2">
          <button onClick={addCustom} disabled={!custom.name.trim()} className="px-3 py-2 rounded-lg bg-amber-600 disabled:bg-slate-400 text-white text-sm font-bold" data-testid="ms-custom-add">Add</button>
          <button onClick={() => setCustom(null)} className="px-3 py-2 rounded-lg bg-white border border-amber-300 text-amber-900 text-sm font-semibold" data-testid="ms-custom-cancel">Cancel</button>
        </div>
      </div>
    );
  }

  const exact = results.some(r => (r.name || '').toLowerCase() === q.trim().toLowerCase());
  const Tabs = () => (
    <div className="flex gap-1 mb-2" data-testid="ms-picker-tabs">
      {[['consumable', 'Consumables'], ['equipment', '365 Equipment']].map(([m, lbl]) => (
        <button key={m} type="button" onClick={() => setMode(m)}
          className={`px-3 py-1 rounded-full text-xs font-bold ${mode === m ? 'bg-white text-slate-900' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid={`ms-picker-tab-${m}`}>{lbl}</button>
      ))}
    </div>
  );
  if (mode === 'equipment') {
    return (
      <div>
        <Tabs />
        <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3" data-testid="ms-equipment-picker">
          <p className="text-xs text-blue-900 font-semibold mb-2 inline-flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> 365-owned equipment — charged at the listed Daily / Weekly / Monthly rate</p>
          <EquipmentRatePicker value={eqPick} onChange={setEqPick} testid="ms-eq"
            onAdd={(p) => { onAdd({ rate_id: p.rate_id, rate_basis: p.rate_basis, qty: p.qty, name: p.name, unit: p.rate_basis, unit_price: p.unit_price, kind: 'equipment', category: 'Equipment rental' }); setEqPick({ rate_basis: p.rate_basis, qty: 1 }); }} />
        </div>
      </div>
    );
  }
  return (
    <div className="relative" ref={box}>
      <Tabs />
      <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
      <input value={q} disabled={disabled} onChange={e => setQ(e.target.value)} onFocus={() => results.length && setOpenList(true)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (results[0]) pick(results[0]); else if (q.trim()) startCustom(); } if (e.key === 'Escape') setOpenList(false); }}
        placeholder="Add an item — search the consumables list (e.g. 7018, zip cut, anchor 3/4)…"
        className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-sm outline-none focus:ring-2 focus:ring-emerald-400 disabled:bg-slate-100" data-testid="ms-item-search" />
      {openList && q.trim() && (
        <div className="absolute z-30 mt-1 w-full max-h-80 overflow-auto rounded-xl bg-white shadow-2xl border border-slate-200 divide-y divide-slate-100" data-testid="ms-item-results">
          {results.map(c => (
            <button key={c.id} onClick={() => pick(c)} className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center justify-between gap-3" data-testid={`ms-item-result-${c.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{c.name}{c.size_variant ? <span className="text-slate-500 font-normal"> · {c.size_variant}</span> : null}</p>
                <p className="text-[11px] text-slate-500 truncate">{c.category}{c.part_number ? ` · #${c.part_number}` : ''} · per {c.unit}</p>
              </div>
              <span className={`text-sm font-mono font-bold flex-shrink-0 ${c.unit_price > 0 ? 'text-emerald-700' : 'text-amber-600'}`}>{c.unit_price > 0 ? money(c.unit_price) : 'no price'}</span>
            </button>
          ))}
          {!exact && (
            <button onClick={startCustom} className="w-full text-left px-3 py-2.5 hover:bg-amber-50 text-sm text-amber-800 font-semibold flex items-center gap-2" data-testid="ms-item-custom">
              <PlusCircle className="w-4 h-4" /> {results.length ? 'Not listed? ' : 'Nothing in the catalog matches. '}Add “{q.trim()}” as a one-off item
            </button>
          )}
        </div>
      )}
    </div>
  );
}
